
import { setSelectedAccess } from "@core/slice/UsersSlice.ts";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { addLocale } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { classNames } from "primereact/utils";
import React from "react";
import { DynamicOptionService, DynamicOptionCategory } from "@core/services/dynamicOption.service.ts";
import { DynamicOptionCategory as DynCat } from "@core/modeles/dynamicOption.model.ts";
import { fetchStandardDynamicOptions } from "@core/actions/dynamicOptionAction.ts";
import { incrementDynamicOption, makeSelectStandardLabels } from "@core/slice/dynamicOptionsCountSlice.ts";
import { logAudit } from "@utils/auditLog.ts";
import { FileUploadSelectEvent } from 'primereact/fileupload';
import {
  LegalStatusList,
  LegalStatus,
  ConcernedBranches,
  ReferenceDocuments,
  ConcernedBranchesList,
  ReferenceDocumentsList,
  PartnershipType,
  PartnershipTypeList,
  InformationChannel,
  InformationChannelList,
  PartenerModel,
} from "@modules/setting/userManagement/profil.model.ts";
import { getPhoneMaxLength } from "../user.model.ts";

// Fonction utilitaire locale pour retirer les accents
function removeDiacritics(str: string) {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

interface ExternalAgentFormProps {
  setIsExternalAgent: (a: boolean) => void;
  isExternalAgent: boolean;
  setFormData: (a: PartenerModel) => void;
  formData: PartenerModel;
  setFile: (a: File) => void;
  isEditMode: boolean;
  resetFormAndErrors: () => void; 
}

const ExternalAgentForm: React.FC<ExternalAgentFormProps> = ({
  setIsExternalAgent,
  isExternalAgent,
  setFormData,
  formData,
  setFile,
  isEditMode,
  resetFormAndErrors,
}) => {
  addLocale("fr", {
    firstDayOfWeek: 1,
    dayNames: [
      "dimanche",
      "lundi",
      "mardi",
      "mercredi",
      "jeudi",
      "vendredi",
      "samedi",
    ],
    dayNamesShort: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: [
      "Janvier ",
      "Février ",
      "Mars ",
      "Avril ",
      "Mai ",
      "Juin ",
      "Juillet ",
      "Août ",
      "Septembre ",
      "Octobre ",
      "Novembre ",
      "Décembre ",
    ],
    monthNamesShort: [
      "janv",
      "févr",
      "mars",
      "avr",
      "mai",
      "juin",
      "juil",
      "août",
      "sept",
      "oct",
      "nov",
      "déc",
    ],
    today: "Aujourd'hui",
    clear: "Effacer",
  });

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap text-2xl">
        Formulaire de fiche partenaire
      </span>
    </div>
  );
  const dispatch = useAppDispatch();
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);

  // Options dynamiques via Redux store (local counts + backend, visible dès saisie)
  const dynamicLegalStatus = useAppSelector(makeSelectStandardLabels(DynCat.LEGAL_STATUS));
  const dynamicConcernedBranches = useAppSelector(makeSelectStandardLabels(DynCat.CONCERNED_BRANCHES));
  const dynamicPartnershipType = useAppSelector(makeSelectStandardLabels(DynCat.PARTNERSHIP_TYPE));
  const dynamicReferenceDocuments = useAppSelector(makeSelectStandardLabels(DynCat.REFERENCE_DOCUMENTS));
  const dynamicInformationChannel = useAppSelector(makeSelectStandardLabels(DynCat.INFORMATION_CHANNEL));

  // États séparés pour la saisie manuelle dans "Autre" (indépendant des options dynamiques)
  const [isOtherManualLegalStatus, setIsOtherManualLegalStatus] = React.useState(false);
  const [manualOtherLegalStatus, setManualOtherLegalStatus] = React.useState('');
  const [isOtherManualConcernedBranches, setIsOtherManualConcernedBranches] = React.useState(false);
  const [manualOtherConcernedBranches, setManualOtherConcernedBranches] = React.useState('');
  const [isOtherManualPartnershipType, setIsOtherManualPartnershipType] = React.useState(false);
  const [manualOtherPartnershipType, setManualOtherPartnershipType] = React.useState('');
  const [isOtherManualReferenceDoc, setIsOtherManualReferenceDoc] = React.useState(false);
  const [manualOtherReferenceDoc, setManualOtherReferenceDoc] = React.useState('');
  const [isOtherManualInfoChannel, setIsOtherManualInfoChannel] = React.useState(false);
  const [manualOtherInfoChannel, setManualOtherInfoChannel] = React.useState('');

  React.useEffect(() => {
    dispatch(fetchStandardDynamicOptions(DynCat.LEGAL_STATUS));
    dispatch(fetchStandardDynamicOptions(DynCat.CONCERNED_BRANCHES));
    dispatch(fetchStandardDynamicOptions(DynCat.PARTNERSHIP_TYPE));
    dispatch(fetchStandardDynamicOptions(DynCat.REFERENCE_DOCUMENTS));
    dispatch(fetchStandardDynamicOptions(DynCat.INFORMATION_CHANNEL));
  }, [dispatch]);

  const [errors, setErrors] = React.useState({
    organizationName: "",
    acronym: "",
    // interventionZone: "", // Champ désactivé (ne pas utiliser)
    concernedBranches: "",
    otherConcernedBranches:"",
    physicalOrPostalAddress: "",
    mainPhoneNumber: "",
    generalEmailAddress: "",
    website: "",
    partnershipTypes: "",
    otherPartnershipType:"",
    reportingChannels: "",
    legalStatuses:"",
    otherLegalStatus:"",
    otherReferenceDocument: "",
    otherReportingChannel:""
  });

  // Fonction utilitaire pour standardiser la valeur (casse, accents)
  const normalizeLabel = (label: string) => removeDiacritics(label.trim().toLowerCase());

  // Helper : cocher/décocher une option dynamique → stocke dans OTHER + other...
  const toggleDynamicOption = (
    opt: string,
    enumArray: string[],
    otherField: string,
    otherEnumCode: string,
    updateData: (newArray: string[], newOther: string) => void,
    clearError: () => void
  ) => {
    const currentOthers = otherField ? otherField.split(',').map(s => s.trim()).filter(s => s) : [];
    const isChecked = currentOthers.includes(opt);
    let newOthers: string[];
    let newArray = [...enumArray];

    if (isChecked) {
      newOthers = currentOthers.filter(v => v !== opt);
      if (newOthers.length === 0 && newArray.includes(otherEnumCode)) {
        newArray = newArray.filter(s => s !== otherEnumCode);
      }
    } else {
      newOthers = [...currentOthers, opt];
      if (!newArray.includes(otherEnumCode)) {
        newArray.push(otherEnumCode);
      }
    }
    updateData(newArray, newOthers.join(', '));
    clearError();
  };

  // Ajout dynamique d'une option si saisie dans un champ "Autre"
  const handleDynamicOption = async (category: DynamicOptionCategory, label: string, existingOptions: string[] = []) => {
    if (!label) return;
    
    // Split par virgule pour gérer plusieurs valeurs
    const values = label.split(',').map(v => v.trim()).filter(v => v);
    
    for (const value of values) {
      const normalized = normalizeLabel(value);
      
      // Ne pas réenregistrer si l'option existe déjà dans la liste dynamique
      if (existingOptions.some(opt => normalizeLabel(opt) === normalized)) {
        continue;
      }

      // Increment du compteur local (localStorage + Redux) → checkbox visible dès la 1ère saisie
      dispatch(incrementDynamicOption({ category: category as string, label: value }));
      
      try {
        await new DynamicOptionService().addOption({ category, labelOriginal: normalized });
        logAudit({
          action: 'ADD_DYNAMIC_OPTION',
          userId: '', // à compléter si user context
          username: '', // à compléter si user context
          targetType: 'DynamicOption',
          details: `Ajout option dynamique: ${category} - ${normalized}`,
          timestamp: new Date().toISOString(),
        });
      } catch (e) {
        console.error('[API] addOption error:', e);
        // Option déjà existante ou erreur, ignorer silencieusement
      }
    }
  };

  const handleSavePartenaire = async () => {
    let formErrors = { ...errors };

    if (!formData.organizationName?.trim()) {
      formErrors.organizationName =
        "Veuillez remplir le champ nom de partenaire ";
    } else formErrors.organizationName = "";

    if (!formData.legalStatuses?.length) {
      formErrors.legalStatuses =
        "Veuillez choisir au moins un statut juridique";
    } else formErrors.legalStatuses = "";

    if (!formData.concernedBranches?.length) {
      formErrors.concernedBranches =
        "Veuillez choisir au moins un Branches concernées";
    } else formErrors.concernedBranches = "";

    if (!formData.partnershipTypes?.length) {
      formErrors.partnershipTypes =
        "Veuillez choisir au moins un Type de partenariat";
    } else formErrors.partnershipTypes = "";

    if (!formData.reportingChannels?.length) {
      formErrors.reportingChannels =
        "Veuillez choisir au moins un canal de remontée des informations";
    } else formErrors.reportingChannels = "";

    // interventionZone désactivé : plus de contrôle

    if (!formData.physicalOrPostalAddress?.trim()) {
      formErrors.physicalOrPostalAddress =
        "Veuillez choisir au moins un Adresse physique / postale";
    } else formErrors.physicalOrPostalAddress = "";

    if (!formData.mainPhoneNumber?.trim()) {
      formErrors.mainPhoneNumber = "Veuillez remplir un numéro téléphone";
    } else formErrors.mainPhoneNumber = "";

    if (!formData.generalEmailAddress?.trim()) {
      formErrors.generalEmailAddress = "Veuillez remplir un email";
    } else formErrors.generalEmailAddress = "";
  

  // Ajout dynamique si champ "Autre" rempli
  if (formData.legalStatuses?.includes(LegalStatus.OTHER) && formData.otherLegalStatus?.trim()) {
    await handleDynamicOption(DynamicOptionCategory.LegalStatus, formData.otherLegalStatus, dynamicLegalStatus);
  } else if (formData.legalStatuses?.includes(LegalStatus.OTHER) && !formData.otherLegalStatus?.trim()) {
    formErrors = { ...errors, otherLegalStatus: 'Veuillez préciser le type de Status.' };
  }

  if (formData.concernedBranches?.includes(ConcernedBranches.OTHER) && formData.otherConcernedBranches?.trim()) {
    await handleDynamicOption(DynamicOptionCategory.ConcernedBranches, formData.otherConcernedBranches, dynamicConcernedBranches);
  } else if (formData.concernedBranches?.includes(ConcernedBranches.OTHER) && !formData.otherConcernedBranches?.trim()) {
    formErrors = { ...errors, otherConcernedBranches: 'Veuillez préciser le type de Branche.' };
  }

  if (formData.partnershipTypes?.includes(PartnershipType.OTHER) && formData.otherPartnershipType?.trim()) {
    await handleDynamicOption(DynamicOptionCategory.PartnershipType, formData.otherPartnershipType, dynamicPartnershipType);
  } else if (formData.partnershipTypes?.includes(PartnershipType.OTHER) && !formData.otherPartnershipType?.trim()) {
    formErrors = { ...errors, otherPartnershipType: 'Veuillez préciser le type de partenariat.' };
  }

  if (formData.referenceDocuments?.includes(ReferenceDocuments.OTHER) && formData.otherReferenceDocument?.trim()) {
    await handleDynamicOption(DynamicOptionCategory.ReferenceDocuments, formData.otherReferenceDocument, dynamicReferenceDocuments);
  } else if (formData.referenceDocuments?.includes(ReferenceDocuments.OTHER) && !formData.otherReferenceDocument?.trim()) {
    formErrors = { ...errors, otherReferenceDocument: 'Veuillez préciser le type de Document.' };
  }

  if (formData.reportingChannels?.includes(InformationChannel.OTHER) && formData.otherReportingChannel?.trim()) {
    await handleDynamicOption(DynamicOptionCategory.InformationChannel, formData.otherReportingChannel, dynamicInformationChannel);
  } else if (formData.reportingChannels?.includes(InformationChannel.OTHER) && !formData.otherReportingChannel?.trim()) {
    formErrors = { ...errors, otherReportingChannel: 'Veuillez préciser le type de plateforme.' };
  }

    setErrors(formErrors);
    const hasErrors = Object.values(formErrors).some((val) => val !== "");
    if (!hasErrors) {
      const partnerData: PartenerModel = {
        organizationName: formData.organizationName,
        acronym: formData.acronym,
        legalStatuses: formData.legalStatuses,
        otherLegalStatus: formData.otherLegalStatus ?? "",
        // interventionZone: formData.interventionZone, // Champ désactivé (ne pas utiliser)
        concernedBranches: formData.concernedBranches,
        otherConcernedBranches: formData.otherConcernedBranches ?? "",
        physicalOrPostalAddress: formData.physicalOrPostalAddress,
        mainPhoneNumber: formData.mainPhoneNumber,
        generalEmailAddress: formData.generalEmailAddress,
        website: formData.website,
        partnershipTypes: formData.partnershipTypes,
        otherPartnershipType: formData.otherPartnershipType ?? "",
        interventionDomains: formData.interventionDomains,
        rolesAndResponsibilities: formData.rolesAndResponsibilities,
        collaborationStartDate: formData.collaborationStartDate,
        collaborationEndDate: formData.collaborationEndDate,
        referenceDocuments: formData.referenceDocuments,
        otherReferenceDocument: formData.otherReferenceDocument ?? "",
        reportingChannels: formData.reportingChannels,
        otherReportingChannel: formData.otherReportingChannel ?? "",
      };
      setFormData(partnerData);
      setIsExternalAgent(false);
    }
  };

  const handleBlurPhone = (e: React.FocusEvent<HTMLInputElement>) => {
      let updatedErrors = { ...errors };
      const value = e.target.value.replace(/\s/g, "");
  
      const localPhoneRegex = /^(032|033|034|038|037|020)\d{7}$/;
      const internationalPhoneRegex = /^\+261(32|33|34|38|37|20)\d{7}$/;
  
      if (
        !localPhoneRegex.test(value) &&
        !internationalPhoneRegex.test(value) &&
        value !== ""
      ) {
        updatedErrors.mainPhoneNumber =
          "Numéro valide: 032|033|034|038|037|020xxxxxxx  +261xxxxxxxxx (ex: 032xxxxxxx ou +26132xxxxxxx)";
      } else {
        updatedErrors.mainPhoneNumber = "";
      }
  
      setErrors(updatedErrors);
    };

  const SiteWeburlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(:\d+)?(\/[^\s?#]*)?(\?[^\s#]*)?(#[^\s]*)?$/;

  const handleSiteWeb = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value;
    if (!SiteWeburlRegex.test(value)) {
      updatedErrors.website =
        "Caractères spéciaux non autorisés dans le site web.";
    } else if (!SiteWeburlRegex.test(value)) {
      updatedErrors.website = "Format site web invalide.";
    } else {
      updatedErrors.website = "";
    }

    setErrors(updatedErrors);
  };

  const emailRegex = /^[a-zA-Z0-9@.]+$/;
  const emailStructureRegex =
    /^[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)+$/;

  const handleBlurEmail = (e: React.FocusEvent<HTMLInputElement>) => {
    let updatedErrors = { ...errors };
    const value = e.target.value;
    if (!emailRegex.test(value)) {
      updatedErrors.generalEmailAddress =
        "Caractères spéciaux non autorisés dans l'email.";
    } else if (!emailStructureRegex.test(value)) {
      updatedErrors.generalEmailAddress = "Format d'email invalide.";
    } else {
      updatedErrors.generalEmailAddress = "";
    }

    setErrors(updatedErrors);
  };

  const footerContent = (
    <div className="flex justify-end">
      <Button
        onClick={() => {
          setIsExternalAgent(false);
          if (!isEditMode) {
            dispatch(setSelectedAccess([]));
            resetFormAndErrors();
          }
          setErrors({
            organizationName: "",
            acronym: "",
            // interventionZone: "", // Champ désactivé (ne pas utiliser)
            concernedBranches: "",
            otherConcernedBranches: "",
            physicalOrPostalAddress: "",
            mainPhoneNumber: "",
            generalEmailAddress: "",
            partnershipTypes: "",
            otherPartnershipType: "",
            website:"",
            reportingChannels: "",
            legalStatuses: "",
            otherLegalStatus: "",
            otherReferenceDocument: "",
            otherReportingChannel: "",
          });
        }}
        className={classNames("btn btn-secondary w-40 mr-2", {
          "btn-secondary-disabled": false,
        })}
      >
        Annuler
      </Button>
      <Button
        onClick={handleSavePartenaire}
        className={classNames("btn btn-primary w-40 ml-2", {
          "btn-primary-disabled": false,
        })}
      >
        Enregistrer
      </Button>
    </div>
  );
  
  const formatDateToYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFileSelect = (e: FileUploadSelectEvent) => {
    if (e.files.length > 0) {
      setFile(e.files[0]);
    }
  };

  return (
    <div className="card flex justify-content-center">
      <Dialog
        visible={isExternalAgent}
        modal
        header={headerElement}
        footer={footerContent}
        style={{ width: "60rem" }}
        onHide={() => {
          setIsExternalAgent(false);
          if (!isEditMode) {
            dispatch(setSelectedAccess([]));
            resetFormAndErrors();
          } else {
            setErrors({
              organizationName: "",
              acronym: "",
              // interventionZone: "", // Champ désactivé (ne pas utiliser)
              concernedBranches: "",
              otherConcernedBranches: "",
              physicalOrPostalAddress: "",
              mainPhoneNumber: "",
              generalEmailAddress: "",
              website: "",
              partnershipTypes: "",
              otherPartnershipType: "",
              reportingChannels: "",
              legalStatuses: "",
              otherLegalStatus: "",
              otherReferenceDocument: "",
              otherReportingChannel: "",
            });
          }
        }}
      >
        <p className="m-0 text-xl font-semibold mb-2">
          1. Informations générales
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col mt-1">
            <label>Nom de l'organisation(Obligatoire): &nbsp;</label>
            <InputText
              name="nom"
              className="w-full my-2"
              maxLength={255}
              placeholder="Nom de l'organisation"
              value={formData.organizationName}
              onChange={(e) => {
                setFormData({ ...formData, organizationName: e.target.value });
                if (e.target.value !== "") {
                  setErrors({ ...errors, organizationName: "" });
                }
              }}
            />
            {errors.organizationName && (
              <small className="text-red-600">{errors.organizationName}</small>
            )}
          </div>
          <div className="flex flex-col mt-1">
            <label>Acronyme (si différent): </label>
            <InputText
              name="acronyme"
              className="w-full my-2"
              maxLength={255}
              placeholder="Acronyme"
              value={formData.acronym}
              onChange={(e) => {
                setFormData({ ...formData, acronym: e.target.value });
              }}
            />
            {errors.acronym && (
              <small className="text-red-600">{errors.acronym}</small>
            )}
          </div>
          <div className="flex-1 gap-1 mt-1">
            <label className="block font-medium mb-2">Statut juridique (Obligatoire):</label>
            <div className="flex flex-col mt-3">
              {/* Options dynamiques (checkboxes visibles) */}
              {dynamicLegalStatus.length > 0 && dynamicLegalStatus.map((opt: string) => (
                <div key={opt} className="flex items-center mb-4">
                  <Checkbox
                    inputId={`dynamic-legal-${opt}`}
                    checked={formData.otherLegalStatus?.split(',').map(s => s.trim()).includes(opt)}
                    onChange={() => toggleDynamicOption(
                      opt,
                      formData.legalStatuses ?? [],
                      formData.otherLegalStatus ?? '',
                      LegalStatus.OTHER,
                      (arr, val) => setFormData({ ...formData, legalStatuses: arr, otherLegalStatus: [val, manualOtherLegalStatus].filter(s => s).join(', ') }),
                      () => setErrors({ ...errors, legalStatuses: '', otherLegalStatus: '' })
                    )}
                  />
                  <label htmlFor={`dynamic-legal-${opt}`} className="ml-2">{opt}</label>
                </div>
              ))}
              {/* Les statuts standards sauf 'Autre' */}
              {LegalStatusList.filter((status) => status.code !== LegalStatus.OTHER).map((status) => (
                <div key={status.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={status.code}
                    checked={formData.legalStatuses?.includes(status.code)}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        setErrors({ ...errors, legalStatuses: "" });
                      }
                      const isChecked = formData.legalStatuses?.includes(status.code);
                      const newStatuses = isChecked
                        ? formData.legalStatuses.filter((s: string) => s !== status.code)
                        : [...(formData.legalStatuses ?? []), status.code];
                      setFormData({ ...formData, legalStatuses: newStatuses });
                    }}
                  />
                  <label htmlFor={status.code} className="ml-2">{status.name}</label>
                </div>
              ))}
              {/* L'option 'Autre' (saisie manuelle uniquement) */}
              {LegalStatusList.filter((status) => status.code === LegalStatus.OTHER).map((status) => (
                <div key={status.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={status.code}
                    checked={isOtherManualLegalStatus}
                    onChange={() => {
                      if (isOtherManualLegalStatus) {
                        setIsOtherManualLegalStatus(false);
                        setManualOtherLegalStatus('');
                        const dynamicPart = (formData.otherLegalStatus || '').split(',').map(s => s.trim()).filter(s => s && dynamicLegalStatus.includes(s)).join(', ');
                        setFormData({ ...formData,
                          legalStatuses: dynamicPart ? formData.legalStatuses ?? [] : (formData.legalStatuses ?? []).filter(s => s !== LegalStatus.OTHER),
                          otherLegalStatus: dynamicPart,
                        });
                      } else {
                        setIsOtherManualLegalStatus(true);
                        const newStatuses = formData.legalStatuses?.includes(LegalStatus.OTHER)
                          ? formData.legalStatuses
                          : [...(formData.legalStatuses ?? []), LegalStatus.OTHER];
                        setFormData({ ...formData, legalStatuses: newStatuses });
                        setErrors({ ...errors, legalStatuses: '' });
                      }
                    }}
                  />
                  <label htmlFor={status.code} className="ml-2">{status.name}</label>
                  {isOtherManualLegalStatus && (
                    <div className="ml-2 flex flex-col gap-1">
                      <InputText
                        name="otherLegalStatus"
                        className="ml-2"
                        maxLength={255}
                        value={manualOtherLegalStatus}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualOtherLegalStatus(val);
                          const dynamicPart = (formData.otherLegalStatus || '').split(',').map(s => s.trim()).filter(s => s && dynamicLegalStatus.includes(s)).join(', ');
                          setFormData({ ...formData, otherLegalStatus: [dynamicPart, val].filter(s => s).join(', ') });
                          if (val) setErrors({ ...errors, otherLegalStatus: '' });
                        }}
                        placeholder="Entrez une autre status"
                      />
                      {errors.otherLegalStatus && (
                        <span className="text-red-600 text-sm">{errors.otherLegalStatus}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {errors.legalStatuses && (
              <small className="text-red-600">{errors.legalStatuses}</small>
            )}
            </div>
          </div>
          {/* interventionZone désactivé : champ masqué du formulaire */}
          <div className="flex-1 gap-1 mt-1">
            <label className="block font-medium mb-2">
              Branches concernées (Obligatoire):
            </label>
            <div className="flex flex-col mt-3">
              {/* Options dynamiques (checkboxes visibles) */}
              {dynamicConcernedBranches.length > 0 && dynamicConcernedBranches.map((opt: string) => (
                <div key={opt} className="flex items-center mb-4">
                  <Checkbox
                    inputId={`dynamic-branch-${opt}`}
                    checked={formData.otherConcernedBranches?.split(',').map(s => s.trim()).includes(opt)}
                    onChange={() => toggleDynamicOption(
                      opt,
                      formData.concernedBranches ?? [],
                      formData.otherConcernedBranches ?? '',
                      ConcernedBranches.OTHER,
                      (arr, val) => setFormData({ ...formData, concernedBranches: arr, otherConcernedBranches: [val, manualOtherConcernedBranches].filter(s => s).join(', ') }),
                      () => setErrors({ ...errors, concernedBranches: '', otherConcernedBranches: '' })
                    )}
                  />
                  <label htmlFor={`dynamic-branch-${opt}`} className="ml-2">{opt}</label>
                </div>
              ))}
              {/* Les branches standards sauf 'Autre' */}
              {ConcernedBranchesList.filter((branch) => branch.code !== ConcernedBranches.OTHER).map((branch) => (
                <div key={branch.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={branch.code}
                    checked={formData.concernedBranches?.includes(branch.code) ?? false}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        setErrors({ ...errors, concernedBranches: "" });
                      }
                      const isChecked = formData.concernedBranches?.includes(branch.code);
                      const newBranches = isChecked
                        ? formData.concernedBranches.filter((b: string) => b !== branch.code)
                        : [...(formData.concernedBranches ?? []), branch.code];
                      setFormData({ ...formData, concernedBranches: newBranches });
                    }}
                  />
                  <label htmlFor={branch.code} className="ml-2">{branch.name}</label>
                </div>
              ))}
              {/* L'option 'Autre' et son champ texte */}
              {ConcernedBranchesList.filter((branch) => branch.code === ConcernedBranches.OTHER).map((branch) => (
                <div key={branch.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={branch.code}
                    checked={isOtherManualConcernedBranches}
                    onChange={() => {
                      if (isOtherManualConcernedBranches) {
                        setIsOtherManualConcernedBranches(false);
                        setManualOtherConcernedBranches('');
                        const dynamicPart = (formData.otherConcernedBranches || '').split(',').map(s => s.trim()).filter(s => s && dynamicConcernedBranches.includes(s)).join(', ');
                        setFormData({ ...formData,
                          concernedBranches: dynamicPart ? formData.concernedBranches ?? [] : (formData.concernedBranches ?? []).filter(s => s !== ConcernedBranches.OTHER),
                          otherConcernedBranches: dynamicPart,
                        });
                      } else {
                        setIsOtherManualConcernedBranches(true);
                        const newBranches = formData.concernedBranches?.includes(ConcernedBranches.OTHER)
                          ? formData.concernedBranches
                          : [...(formData.concernedBranches ?? []), ConcernedBranches.OTHER];
                        setFormData({ ...formData, concernedBranches: newBranches });
                        setErrors({ ...errors, concernedBranches: '' });
                      }
                    }}
                  />
                  <label htmlFor={branch.code} className="ml-2">Autre:</label>
                  {isOtherManualConcernedBranches && (
                    <div className="ml-2 flex flex-col gap-1">
                      <InputText
                        type="text"
                        name="otherConcernedBranches"
                        className="ml-2"
                        maxLength={255}
                        value={manualOtherConcernedBranches}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualOtherConcernedBranches(val);
                          const dynamicPart = (formData.otherConcernedBranches || '').split(',').map(s => s.trim()).filter(s => s && dynamicConcernedBranches.includes(s)).join(', ');
                          setFormData({ ...formData, otherConcernedBranches: [dynamicPart, val].filter(s => s).join(', ') });
                          if (val) setErrors({ ...errors, otherConcernedBranches: '' });
                        }}
                        placeholder="Entrez une autre branche"
                      />
                      {errors.otherConcernedBranches && (
                        <span className="text-red-600 text-sm">{errors.otherConcernedBranches}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {errors.concernedBranches && (
                <small className="text-red-600">{errors.concernedBranches}</small>
              )}
            </div>
          </div>
        </div>
        <p className="m-0 text-xl font-semibold">2. Coordonnées</p>
        <div className="flex flex-col gap-3 mb-3">
          <div className="flex flex-col mt-1">
            <label>Adresse physique / postale(Obligatoire): &nbsp;</label>
            <InputText
              name="adresse"
              className="w-full my-2"
              maxLength={255}
              placeholder="Adresse physique / postale"
              value={formData.physicalOrPostalAddress}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  physicalOrPostalAddress: e.target.value,
                });
                if (e.target.value !== "") {
                  setErrors({ ...errors, physicalOrPostalAddress: "" });
                }
              }}
            />
            {errors.physicalOrPostalAddress && (
              <small className="text-red-600">
                {errors.physicalOrPostalAddress}
              </small>
            )}
          </div>
          <div className="flex flex-col mt-1">
            <label>Numéro téléphone(Obligatoire): &nbsp;</label>
            <InputText
              name="Numero"
              className="w-full my-2"
              placeholder="Numéro téléphone principal"
              value={formData.mainPhoneNumber}
              onChange={(e) => {
                setFormData({ ...formData, mainPhoneNumber: e.target.value });
                if (e.target.value !== "") {
                  setErrors({ ...errors, mainPhoneNumber: "" });
                }
              }}
              onBlur={handleBlurPhone}
              maxLength={getPhoneMaxLength(formData.mainPhoneNumber)}
            />
            {errors.mainPhoneNumber && (
              <small className="text-red-600">{errors.mainPhoneNumber}</small>
            )}
          </div>
          <div className="flex flex-col mt-1">
            <label>Adresse e-mail(Obligatoire): &nbsp;</label>
            <InputText
              name="Numero"
              className="w-full my-2"
              maxLength={255}
              placeholder="exemple@gmail.com"
              value={formData.generalEmailAddress}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  generalEmailAddress: e.target.value,
                });
                if (e.target.value !== "") {
                  setErrors({ ...errors, generalEmailAddress: "" });
                }
              }}
              onBlur={handleBlurEmail}
            />
            {errors.generalEmailAddress && (
              <small className="text-red-600">
                {errors.generalEmailAddress}
              </small>
            )}
          </div>
          <div className="flex flex-col mt-1">
            <label>Site web: &nbsp;</label>
            <InputText
              name="site"
              className="w-full my-2"
              maxLength={255}
              placeholder="https://exemple.net/"
              value={formData.website}
              onChange={(e) => {
                setFormData({ ...formData, website: e.target.value });
              }}
              onBlur={handleSiteWeb}
            />
            {errors.website && (
              <small className="text-red-600">
                {errors.website}
              </small>
            )}
          </div>
        </div>
        <p className="m-0 text-xl font-semibold">3. Détails du partenariat</p>
        <div className="flex flex-col gap-3 mb-3">
          <div className="flex-1 gap-1 mt-1">
            <label className="block font-medium mb-2">
              Types de partenariat(Obligatoire):
            </label>
            <div className="flex flex-col mt-3">
              {/* Options dynamiques (checkboxes visibles) */}
              {dynamicPartnershipType.length > 0 && dynamicPartnershipType.map((opt: string) => (
                <div key={opt} className="flex items-center mb-4">
                  <Checkbox
                    inputId={`dynamic-partner-${opt}`}
                    checked={formData.otherPartnershipType?.split(',').map(s => s.trim()).includes(opt)}
                    onChange={() => toggleDynamicOption(
                      opt,
                      formData.partnershipTypes ?? [],
                      formData.otherPartnershipType ?? '',
                      PartnershipType.OTHER,
                      (arr, val) => setFormData({ ...formData, partnershipTypes: arr, otherPartnershipType: [val, manualOtherPartnershipType].filter(s => s).join(', ') }),
                      () => setErrors({ ...errors, partnershipTypes: '', otherPartnershipType: '' })
                    )}
                  />
                  <label htmlFor={`dynamic-partner-${opt}`} className="ml-2">{opt}</label>
                </div>
              ))}
              {/* Les types standards sauf 'Autre' */}
              {PartnershipTypeList.filter((type) => type.code !== PartnershipType.OTHER).map((type) => (
                <div key={type.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={type.code}
                    checked={formData.partnershipTypes?.includes(type.code)}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        setErrors({ ...errors, partnershipTypes: "" });
                      }
                      const isChecked = formData.partnershipTypes?.includes(type.code);
                      const newTypes = isChecked
                        ? formData.partnershipTypes.filter((t: string) => t !== type.code)
                        : [...(formData.partnershipTypes ?? []), type.code];
                      setFormData({ ...formData, partnershipTypes: newTypes });
                    }}
                  />
                  <label htmlFor={type.code} className="ml-2">{type.name}</label>
                </div>
              ))}
              {/* L'option 'Autre' et son champ texte */}
              {PartnershipTypeList.filter((type) => type.code === PartnershipType.OTHER).map((type) => (
                <div key={type.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={type.code}
                    checked={isOtherManualPartnershipType}
                    onChange={() => {
                      if (isOtherManualPartnershipType) {
                        setIsOtherManualPartnershipType(false);
                        setManualOtherPartnershipType('');
                        const dynamicPart = (formData.otherPartnershipType || '').split(',').map(s => s.trim()).filter(s => s && dynamicPartnershipType.includes(s)).join(', ');
                        setFormData({ ...formData,
                          partnershipTypes: dynamicPart ? formData.partnershipTypes ?? [] : (formData.partnershipTypes ?? []).filter(s => s !== PartnershipType.OTHER),
                          otherPartnershipType: dynamicPart,
                        });
                      } else {
                        setIsOtherManualPartnershipType(true);
                        const newTypes = formData.partnershipTypes?.includes(PartnershipType.OTHER)
                          ? formData.partnershipTypes
                          : [...(formData.partnershipTypes ?? []), PartnershipType.OTHER];
                        setFormData({ ...formData, partnershipTypes: newTypes });
                        setErrors({ ...errors, partnershipTypes: '' });
                      }
                    }}
                  />
                  <label htmlFor={type.code} className="ml-2">Autre :</label>
                  {isOtherManualPartnershipType && (
                    <div className="ml-2 flex flex-col gap-1">
                      <InputText
                        type="text"
                        className="ml-2 border rounded px-2 py-1"
                        placeholder="Précisez"
                        value={manualOtherPartnershipType}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualOtherPartnershipType(val);
                          const dynamicPart = (formData.otherPartnershipType || '').split(',').map(s => s.trim()).filter(s => s && dynamicPartnershipType.includes(s)).join(', ');
                          setFormData({ ...formData, otherPartnershipType: [dynamicPart, val].filter(s => s).join(', ') });
                          if (val) setErrors({ ...errors, otherPartnershipType: '' });
                        }}
                      />
                      {errors.otherPartnershipType && (
                        <span className="text-red-600 text-sm">{errors.otherPartnershipType}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {errors.partnershipTypes && (
                <small className="text-red-600">{errors.partnershipTypes}</small>
              )}
            </div>
          </div>
          <div className="flex flex-col mt-1">
            <label>Domaines d'intervention: </label>
            <InputText
              name="domaine"
              className="w-full my-2"
              maxLength={255}
              placeholder="Domaine d'intervention"
              value={formData.interventionDomains}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  interventionDomains: e.target.value,
                })
              }
            />
          </div>
          <div className="flex flex-col mt-1">
            <label>Rôles et responsabilités: </label>
            <InputTextarea
              placeholder="Rôle et responsabilités"
              autoResize
              rows={5}
              cols={30}
              className="w-full my-2"
              value={formData.rolesAndResponsibilities}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  rolesAndResponsibilities: e.target.value,
                })
              }
            />
          </div>
          <div className="mt-4">
          <h3 className="font-semibold mb-2">Durée de la collaboration : (en année)</h3>
          <div className="flex justify-between mt-1">
            <div className="flex items-baseline">
              <label>Date de début : </label>
              <Calendar
                className="ml-3"
                locale="fr"
                placeholder="dd/mm/yyyy"
                id="start-date"
                showIcon
                dateFormat="dd-mm-yy"
                value={
                  formData.collaborationStartDate
                    ? new Date(formData.collaborationStartDate)
                    : null
                }
                onChange={(e) => {
                  const startDate = e.value;
                  if (!startDate) return;
                  const endDate = new Date(startDate);
                  endDate.setFullYear(endDate.getFullYear() + 1);
                  setFormData({
                    ...formData,
                    collaborationStartDate: formatDateToYYYYMMDD(startDate),
                    collaborationEndDate: formatDateToYYYYMMDD(endDate),
                  });
                }}
              />
            </div>
            <div className="flex items-baseline">
              <label>Date de fin : </label>
              <Calendar
                className="ml-3"
                locale="fr"
                placeholder="dd/mm/yyyy"
                id="end-date"
                showIcon
                dateFormat="dd-mm-yy"
                value={
                  formData.collaborationEndDate
                    ? new Date(formData.collaborationEndDate)
                    : null
                }
                readOnlyInput
              />
            </div>
          </div>
          </div>
          <div className="flex-1 gap-1 mt-1">
            <label className="block font-medium mb-2">
              4.Documents de référence fournis :
            </label>
            <div className="flex flex-col mt-3">
              {/* Options dynamiques (checkboxes visibles) */}
              {dynamicReferenceDocuments.length > 0 && dynamicReferenceDocuments.map((opt: string) => (
                <div key={opt} className="flex items-center mb-4">
                  <Checkbox
                    inputId={`dynamic-doc-${opt}`}
                    checked={formData.otherReferenceDocument?.split(',').map(s => s.trim()).includes(opt)}
                    onChange={() => toggleDynamicOption(
                      opt,
                      formData.referenceDocuments ?? [],
                      formData.otherReferenceDocument ?? '',
                      ReferenceDocuments.OTHER,
                      (arr, val) => setFormData({ ...formData, referenceDocuments: arr, otherReferenceDocument: [val, manualOtherReferenceDoc].filter(s => s).join(', ') }),
                      () => setErrors({ ...errors, otherReferenceDocument: '' })
                    )}
                  />
                  <label htmlFor={`dynamic-doc-${opt}`} className="ml-2">{opt}</label>
                </div>
              ))}
              {/* Les documents standards sauf 'Autre' */}
              {ReferenceDocumentsList.filter((doc) => doc.code !== ReferenceDocuments.OTHER).map((doc) => (
                <div key={doc.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={doc.code}
                    checked={formData.referenceDocuments?.includes(doc.code) ?? false}
                    onChange={(e) => {
                      const isChecked = formData.referenceDocuments?.includes(doc.code);
                      const newDocs = isChecked
                        ? formData.referenceDocuments.filter((b: string) => b !== doc.code)
                        : [...(formData.referenceDocuments ?? []), doc.code];
                      setFormData({ ...formData, referenceDocuments: newDocs });
                    }}
                  />
                  <label htmlFor={doc.code} className="ml-2">{doc.name}</label>
                </div>
              ))}
              {/* L'option 'Autre' et son champ texte */}
              {ReferenceDocumentsList.filter((doc) => doc.code === ReferenceDocuments.OTHER).map((doc) => (
                <div key={doc.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={doc.code}
                    checked={isOtherManualReferenceDoc}
                    onChange={() => {
                      if (isOtherManualReferenceDoc) {
                        setIsOtherManualReferenceDoc(false);
                        setManualOtherReferenceDoc('');
                        const dynamicPart = (formData.otherReferenceDocument || '').split(',').map(s => s.trim()).filter(s => s && dynamicReferenceDocuments.includes(s)).join(', ');
                        setFormData({ ...formData,
                          referenceDocuments: dynamicPart ? formData.referenceDocuments ?? [] : (formData.referenceDocuments ?? []).filter(s => s !== ReferenceDocuments.OTHER),
                          otherReferenceDocument: dynamicPart,
                        });
                      } else {
                        setIsOtherManualReferenceDoc(true);
                        const newDocs = formData.referenceDocuments?.includes(ReferenceDocuments.OTHER)
                          ? formData.referenceDocuments
                          : [...(formData.referenceDocuments ?? []), ReferenceDocuments.OTHER];
                        setFormData({ ...formData, referenceDocuments: newDocs });
                      }
                    }}
                  />
                  <label htmlFor={doc.code} className="ml-2">Autre:</label>
                  {isOtherManualReferenceDoc && (
                    <div className="ml-2 flex flex-col gap-1">
                      <InputText
                        type="text"
                        name="otherReferenceDocument"
                        className="ml-2"
                        maxLength={255}
                        value={manualOtherReferenceDoc}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualOtherReferenceDoc(val);
                          const dynamicPart = (formData.otherReferenceDocument || '').split(',').map(s => s.trim()).filter(s => s && dynamicReferenceDocuments.includes(s)).join(', ');
                          setFormData({ ...formData, otherReferenceDocument: [dynamicPart, val].filter(s => s).join(', ') });
                          if (val) setErrors({ ...errors, otherReferenceDocument: '' });
                        }}
                        placeholder="Précisez le type de document "
                      />
                      {errors.otherReferenceDocument && (
                        <span className="text-red-600 text-sm">{errors.otherReferenceDocument}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>          
          <div className="flex items-center mt-2">
            <label>Pièce jointe( Pdf ou doc ou docx):</label>
            <div className="ml-3">
            {uploadedFile ? (
              <div
                className="flex items-center gap-2 p-2 rounded"
                style={{ backgroundColor: '#0886f7' }}
              >
                <Button
                  icon="pi pi-times"
                  rounded
                  text
                  severity="danger"
                  onClick={() => setUploadedFile(null)}
                  className="text-white hover:bg-blue-600"
                />
                <span className="text-sm text-white">{uploadedFile.name}</span>
              </div>
            ) : (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <FileUpload
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    mode="basic"
                    url="/api/upload"
                    chooseLabel="Télécharger un document"
                    onSelect={handleFileSelect}
                    chooseOptions={{
                      className: "bg-blue-500 hover:bg-blue-600 text-white"
                    }}
                    className="border-blue-300"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-4">
            <h3 className="font-semibold mb-2">5. Moyen de communication</h3>
            <label>Moyens utilisés(Obligatoire):</label>
            <div className="flex flex-col gap-3 mt-2">
              {/* Options dynamiques (checkboxes visibles) */}
              {dynamicInformationChannel.length > 0 && dynamicInformationChannel.map((opt: string) => (
                <div key={opt} className="flex items-center mb-4">
                  <Checkbox
                    inputId={`dynamic-channel-${opt}`}
                    checked={formData.otherReportingChannel?.split(',').map(s => s.trim()).includes(opt)}
                    onChange={() => toggleDynamicOption(
                      opt,
                      formData.reportingChannels ?? [],
                      formData.otherReportingChannel ?? '',
                      InformationChannel.OTHER,
                      (arr, val) => setFormData({ ...formData, reportingChannels: arr, otherReportingChannel: [val, manualOtherInfoChannel].filter(s => s).join(', ') }),
                      () => setErrors({ ...errors, reportingChannels: '', otherReportingChannel: '' })
                    )}
                  />
                  <label htmlFor={`dynamic-channel-${opt}`} className="ml-2">{opt}</label>
                </div>
              ))}
              {/* Les canaux standards sauf 'Autre' */}
              {InformationChannelList.filter((channel) => channel.code !== InformationChannel.OTHER).map((channel) => (
                <div key={channel.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={channel.code}
                    checked={formData.reportingChannels?.includes(channel.code)}
                    onChange={(e) => {
                      if (e.target.value !== "") {
                        setErrors({ ...errors, reportingChannels: "" });
                      }
                      const isChecked = formData.reportingChannels?.includes(channel.code);
                      const newChannels = isChecked
                        ? formData.reportingChannels.filter((m: string) => m !== channel.code)
                        : [...(formData.reportingChannels ?? []), channel.code];
                      setFormData({ ...formData, reportingChannels: newChannels });
                    }}
                  />
                  <label htmlFor={channel.code} className="ml-2">{channel.name}</label>
                </div>
              ))}
              {/* L'option 'Autre' et son champ texte */}
              {InformationChannelList.filter((channel) => channel.code === InformationChannel.OTHER).map((channel) => (
                <div key={channel.code} className="flex items-center mb-4">
                  <Checkbox
                    inputId={channel.code}
                    checked={isOtherManualInfoChannel}
                    onChange={() => {
                      if (isOtherManualInfoChannel) {
                        setIsOtherManualInfoChannel(false);
                        setManualOtherInfoChannel('');
                        const dynamicPart = (formData.otherReportingChannel || '').split(',').map(s => s.trim()).filter(s => s && dynamicInformationChannel.includes(s)).join(', ');
                        setFormData({ ...formData,
                          reportingChannels: dynamicPart ? formData.reportingChannels ?? [] : (formData.reportingChannels ?? []).filter(s => s !== InformationChannel.OTHER),
                          otherReportingChannel: dynamicPart,
                        });
                      } else {
                        setIsOtherManualInfoChannel(true);
                        const newChannels = formData.reportingChannels?.includes(InformationChannel.OTHER)
                          ? formData.reportingChannels
                          : [...(formData.reportingChannels ?? []), InformationChannel.OTHER];
                        setFormData({ ...formData, reportingChannels: newChannels });
                        setErrors({ ...errors, reportingChannels: '' });
                      }
                    }}
                  />
                  <label htmlFor={channel.code} className="ml-2">Autre :</label>
                  {isOtherManualInfoChannel && (
                    <div className="ml-2 flex flex-col gap-1">
                      <InputText
                        type="text"
                        className="ml-2 border rounded px-2 py-1"
                        placeholder="Précisez"
                        value={manualOtherInfoChannel}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualOtherInfoChannel(val);
                          const dynamicPart = (formData.otherReportingChannel || '').split(',').map(s => s.trim()).filter(s => s && dynamicInformationChannel.includes(s)).join(', ');
                          setFormData({ ...formData, otherReportingChannel: [dynamicPart, val].filter(s => s).join(', ') });
                          if (val) setErrors({ ...errors, otherReportingChannel: '' });
                        }}
                      />
                      {errors.otherReportingChannel && (
                        <span className="text-red-600 text-sm">{errors.otherReportingChannel}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {errors.reportingChannels && ( <small className="text-red-600">{errors.reportingChannels}</small>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ExternalAgentForm;